import React from 'react';
import BasePage, { IBasePageProps } from '@/pages/BasePage';
import { prefix } from '.';
import ActionType from '@/actions/action-type';
import StoreList from '@/components/StoreList';
import { getAppTheme, setScreenColor, getIntl } from '@/components/App/App';
import Layout from '@/components/Layout';
import { IStore } from '@/actions/store-action';
import paths from '@/routes/paths';
import Icon, { IconType } from '@/components/Icon';
import Status from '@/components/Status';
import LocationTypes from './LocationTypes';
import { animateScroll } from 'react-scroll';
import { handleStoreServiceTypesScroll } from '@/utils/app';
import { setCanAutoProcurementMethodPopup } from '../MenuListPage/MenuListPage';
import { parseRouteParams, getLocalStorageJson, setLocalStorageJson } from '@/utils';
import Constants from '@/constants';
import { setUseLocalCategoryID } from '../brand-list/BrandListPage/BrandListPage';
import { StoreFilterModalContentType, StoreFilterMode } from '@/actions/store-action';
import { handleStoreCategoryTypesScroll } from '@/utils/app';
import Skeleton from './Skeleton';

export interface IStoresByCategoryPageProps extends IBasePageProps {
}

export interface IStoresByCategoryPageState {
  pageInfo: {[categoryID: string]: {page: number; lastPage: boolean}};
  stores?: IStore[];
  loadingStores: boolean;
  loadingFirstPage: boolean;
  showQRCodeScan: boolean;
  selectedLocationID: string;
  byType: string;
  fromPage: string;
  showModal: boolean;
  mode: string;
}

class StoresByLocationPage extends BasePage<IStoresByCategoryPageProps, IStoresByCategoryPageState> {
  loadingData = false;
  lastPage = false;
  selectedLocationID = getLocalStorageJson(Constants.BRANDID) || '0';

  constructor(props: IStoresByCategoryPageProps) {
    super(props);
    const routeParams = parseRouteParams(props);

    this.state = {
      pageInfo: {},
      loadingStores: true,
      loadingFirstPage: true,
      showQRCodeScan: false,
      selectedLocationID: routeParams.locationID || '',
      fromPage: routeParams.fromPage || '',
      byType: routeParams.byType || 'all',
      showModal: false,
      mode: '',
    }
  }

  componentDidMount() {
    const appTheme = getAppTheme();
    if (appTheme.darkBackgroundColor) {
      setScreenColor(appTheme.darkBackgroundColor);
    }

    super.componentDidMount();
    const { dispatch } = this.props;
    const { selectedLocationID, pageInfo } = this.state;
    if(Number(selectedLocationID) !== 0) {
      const newDistrict  = new Set([selectedLocationID]);
      dispatch({
        type: ActionType.SET_STORES_FILTER,
        params: [
          {
            'district': newDistrict,
          }
        ],
      });
    }
    this.handleStoreFilter(selectedLocationID, pageInfo);

    handleStoreServiceTypesScroll(selectedLocationID);

    const pageWrap = window.document.querySelector('.page-content-wrapper-web');
    if (pageWrap) {
      pageWrap.addEventListener('scroll', this.handleScroll, { passive: true });
    } else {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }
  
  componentWillUnmount () {
    window.removeEventListener('scroll', this.handleScroll);
    const pageWrap = window.document.querySelector('.page-content-wrapper-web');
    if (pageWrap) {
      pageWrap.removeEventListener('scroll', this.handleScroll);
    } else {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  getScrollY = () => {
    const pageWrap = window.document.querySelector('.page-content-wrapper-web');
    let scrollY = window.scrollY;
    if (pageWrap) {
      scrollY = pageWrap.scrollTop;
    }

    return scrollY;
  }

  handleScroll = () => {
    const { pageInfo, selectedLocationID, mode, byType } = this.state;
    const newCategoryID = Number(selectedLocationID) === 0 ? '' : selectedLocationID;

    const { page = 1 } = pageInfo[newCategoryID] || {};
    const { dispatch } = this.props;
    if (!this.lastPage && !this.loadingData) {
      const appPages = document.getElementsByClassName('bm-p-stores-by-category');
      if (appPages && appPages.length) {
        const appPage = appPages[0];
        const scrollHeight = appPage.scrollHeight;
        const canScrollHeight = scrollHeight - window.innerHeight;

        if (canScrollHeight - this.getScrollY() < 5 * 200) {
          this.loadingData = true;
          this.setState({loadingStores: true});
          if (mode === 'BYFILTER') {
            dispatch({
              type: ActionType.QUERY_STORES_BY_FILTER,
              mode: StoreFilterMode.CATEGORY,
              categoryID: newCategoryID,
              page: page + 1,
              storeType: byType === 'foodOrdering' ? 'all' : byType,
              callback: (_stores, page, lastPage) => {
                this.loadingData = false;
                this.lastPage = lastPage;
                this.setState({
                  loadingStores: false,
                  pageInfo: {
                    ...pageInfo,
                    [newCategoryID || '']: {
                      page,
                      lastPage,
                    }
                  },
                  mode: 'BYFILTER',
                });

                this.closeLoading();
                dispatch({
                  type: ActionType.LOADING,
                  showLoading: false,
                });
              }
            });
          } else {
            const pageCategoryType = this.getPageCategoryType();
            dispatch({
              type: ActionType.QUERY_STORES_BY_CATEGORY,
              page: page + 1,
              categoryID: newCategoryID,
              pageType: pageCategoryType,
              storeType: byType === 'foodOrdering' ? 'all' : byType,
              callback: (_stores, page, lastPage) => {
                this.loadingData = false;
                this.lastPage = lastPage;
                this.setState({
                  loadingStores: false,
                  pageInfo: {
                    ...pageInfo,
                    [newCategoryID]: {
                      page,
                      lastPage,
                    }
                  },
                });
              }
            });
          }
        }
      }
    }
  }

  handleItemClick = (data: IStore) => {
    const { byType, selectedLocationID } = this.state;
    if (data.isGroup) {
      this.handleGotoRoute({
        path: paths.STORE_GROUP,
        pathParams: {
          storeSlug: data.slug,
        },
      });
    } else {
      let path = paths.MENU_LIST
      if (byType === 'booking') {
        path = paths.BOOKING_NEW;
      } else if (byType === 'queueing') {
        path = paths.QUEUEING_NEW;
      }

      setCanAutoProcurementMethodPopup(true);
      setLocalStorageJson(Constants.BRANDID, selectedLocationID);
      this.handleGotoRoute({
        path,
        pathParams: {
          storeSlug: data.slug,
        },
      });
    }
  }

  handleCategoryClick = (locationID: string, isQuery = true) => {
    const { pageInfo, selectedLocationID } = this.state;
    const { dispatch } = this.props;

    if (selectedLocationID !== locationID) {
      animateScroll.scrollTo(0, {
        duration: 200,
      });
      this.setState({ selectedLocationID: locationID })
    } else { return }

    if (isQuery) {
      this.loadingData = true;
      this.lastPage = false;
      const newLocationID = Number(locationID) === 0 ? '' : locationID;

      this.setState({
        loadingStores: true,
        pageInfo: {
          ...pageInfo,
          [locationID]: {
            lastPage: false,
            page: 1,
          }
        },
      });
      dispatch({
        type: ActionType.SET_STORES_FILTER,
        params: [
          {
            'district': new Set([newLocationID]),
          }
        ],
      });
      this.handleStoreFilter(
        locationID, 
        {
          ...pageInfo,
          [locationID]: {
            lastPage: false,
            page: 1,
          }
        });
    }
  }

  handleStoreFilter = ( selectedLocationID: string, pageInfo: { [categoryID: string]: { page: number; lastPage: boolean } } | undefined ) => {
    this.loadingData = true;
    this.lastPage = false;
    console.info(selectedLocationID)
    this.setState({
      loadingStores: true,
    });


    const newCategoryID = Number(selectedLocationID) === 0 ? '' : selectedLocationID;

    const { dispatch } = this.props;
    const { initData } = this.handleStoreFilterInitData();

    dispatch({
      type: ActionType.LOADING,
      showLoading: true,
    });

    dispatch({
      type: ActionType.QUERY_STORES_BY_FILTER,
      mode: StoreFilterMode.CATEGORY,
      categoryID: newCategoryID,
      replace: true,
      page: 1,
      storeType: 'all',
      callback: (_stores, page, lastPage) => {
        this.loadingData = false;
        this.lastPage = lastPage;
        this.setState({
          loadingStores: false,
          pageInfo: {
            ...pageInfo,
            [newCategoryID]: {
              page,
              lastPage,
            }
          },
          mode: 'BYFILTER',
        });
        if (initData && initData[StoreFilterModalContentType.TYPE]) {
          this.handleCategoryClick(newCategoryID || '0', false);
          handleStoreCategoryTypesScroll(newCategoryID || '0');
        }
        this.closeLoading();
        dispatch({
          type: ActionType.LOADING,
          showLoading: false,
        });
      }
    });
  }

  handleStoreFilterInitData = () => {
    const {
      fromPage,
      byType,
      selectedLocationID,
    } = this.state;

    const newCategoryID = Number(selectedLocationID) === 0 ? '' : selectedLocationID;
    let initData;
    let typeData = '';
    if (fromPage === 'brands') {
      if (byType === 'booking') {
        typeData = 'booking';
      } else if (byType === 'foodOrdering') {
        typeData = 'foodOrdering';
      } else if (byType === 'queueing') {
        typeData = 'queueing';
      } else {
        typeData = 'brands';
      }
      initData = {
        [StoreFilterModalContentType.TYPE]: typeData,
        categoryID: newCategoryID
      }
    }
    return { initData };
  }

  getPageCategoryType = (): string => {
    const { fromPage, byType } = this.state;
    let resString = '';
    if (fromPage === 'brands') {
      if (
        byType !== 'booking' &&
        byType !== 'foodOrdering' &&
        byType !== 'queueing'
      )
      {
        resString = 'brands';
      }
    }
    return resString;
  }

  getStoreFilterMark = () => {
    let markNumber = 0;
    const { reducersState } = this.props;
    const { filterRefStores } = reducersState.store;
    if (filterRefStores.category.size > 0) { markNumber++ }
    if (filterRefStores.brands.size > 0) { markNumber++ }
    if (filterRefStores.district.size > 0) { markNumber++ }
    if (filterRefStores.prices.length > 0) { markNumber++ }
    if (filterRefStores.radius > 0) { markNumber++ }

    return markNumber;
  }

  render() {
    const { reducersState, routeParams } = this.props;
    const {
      loadingStores,
      pageInfo,
      selectedLocationID,
    } = this.state;
    const { platformConfig, platformStoreLocations } = reducersState.app;
    const { typeRefStoresByCategory } = reducersState.store;
    const newLocationID = Number(selectedLocationID) === 0 ? '' : selectedLocationID;
    const stores = typeRefStoresByCategory['all'] ? typeRefStoresByCategory['all'][newLocationID] || [] : [];
    
    const { lastPage = false } = pageInfo[newLocationID] || {};
    const hasStores = stores.length > 0;
    const { validServiceType = [] } = platformConfig;

    return (
      <Layout
        {...this.props}
        className={`bm-p-stores ${prefix} ${validServiceType.length > 1 ? 'has-service-types' : ''}`}
        navContent={getIntl().page.location}
        navIcon={routeParams.mode !== 'dedicated' && <Icon className="back-icon" type={IconType.LEFT_OUTLINED} />}
        onLeftClick={() => {
          if (routeParams.mode !== 'dedicated') {
            setUseLocalCategoryID(true);
            this.handleGoBack({
              path: paths.HOME,
              runGoBack: true,
            });
          }
        }}
      >
        {
          !hasStores &&  loadingStores &&
          <Skeleton/>
        }
        {
          platformStoreLocations.length > 0 &&
          <LocationTypes
            data={platformStoreLocations}
            selected={selectedLocationID}
            className={`${prefix}-service-types`}
            reducersState={reducersState}
            onItemClick={this.handleCategoryClick}
          />
        }
        {
          <>
            {
              hasStores &&
              <>
                <StoreList
                  data={stores}
                  reducersState={reducersState}
                  loading={loadingStores}
                  className={`${prefix}-store-list`}
                  lastPage={lastPage}
                  onItemClick={this.handleItemClick}
                />
                {
                  lastPage &&
                  <div className="no-more-content">
                    {getIntl().page.noMoreContent}
                  </div>
                }
              </>
            }
            {
              !loadingStores && platformConfig.id && !hasStores &&
              <div className="no-stores">
                <Status
                  iconType={IconType.TAG_STORE}
                  title="There are no shops in the city."
                  describe="We are working hard to invite you to join us, please look forward to it!"
                />
              </div>
            }
          </>
        }
      </Layout>
    );
  }
}

export default StoresByLocationPage;
